<script setup>
import { ref, computed, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { resendConfirmation } from '../../../api/auth';

const props = defineProps({
  email: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const router = useRouter();
const store = useStore();

if (!props.email) {
  router.push({ name: 'login' });
}

const RESEND_COOLDOWN_SECONDS = 60;

const globalConfig = computed(() => store.getters['globalConfig/get']);
const isResendingEmail = ref(false);
const cooldownSeconds = ref(0);
const hCaptcha = ref(null);
let captchaToken = '';
let cooldownTimer = null;

const startCooldown = () => {
  cooldownSeconds.value = RESEND_COOLDOWN_SECONDS;
  cooldownTimer = setInterval(() => {
    cooldownSeconds.value -= 1;
    if (cooldownSeconds.value <= 0) clearInterval(cooldownTimer);
  }, 1000);
};

onUnmounted(() => clearInterval(cooldownTimer));

const performResend = async () => {
  isResendingEmail.value = true;
  try {
    await resendConfirmation({
      email: props.email,
      hCaptchaClientResponse: captchaToken,
    });
    useAlert(t('REGISTER.VERIFY_EMAIL.RESEND_SUCCESS'));
    startCooldown();
  } catch {
    useAlert(t('REGISTER.VERIFY_EMAIL.RESEND_ERROR'));
  } finally {
    isResendingEmail.value = false;
    captchaToken = '';
    if (globalConfig.value.hCaptchaSiteKey) {
      hCaptcha.value.reset();
    }
  }
};

const handleResendEmail = () => {
  if (isResendingEmail.value || cooldownSeconds.value > 0) return;
  if (globalConfig.value.hCaptchaSiteKey) {
    hCaptcha.value.execute();
  } else {
    performResend();
  }
};

const resendLabel = computed(() =>
  cooldownSeconds.value > 0
    ? t('REGISTER.VERIFY_EMAIL.RESEND_COOLDOWN', {
        seconds: cooldownSeconds.value,
      })
    : t('REGISTER.VERIFY_EMAIL.RESEND')
);

const goToSignup = () => {
  router.push({ name: 'auth_signup' });
};

const onCaptchaVerified = token => {
  captchaToken = token;
  performResend();
};

const onCaptchaError = () => {
  isResendingEmail.value = false;
  captchaToken = '';
  hCaptcha.value.reset();
};
</script>

<template>
  <main
    class="flex flex-col w-full min-h-screen py-20 lg-canvas sm:px-6 lg:px-8"
  >
    <section
      class="lg-glass-strong sm:mx-auto mt-11 sm:w-full sm:max-w-lg p-11 sm:rounded-[28px]"
    >
      <div class="mb-6">
        <h2 class="text-2xl font-semibold text-n-slate-12">
          {{ $t('REGISTER.VERIFY_EMAIL.TITLE') }}
        </h2>
        <p class="mt-2 text-sm text-n-slate-11">
          {{ $t('REGISTER.VERIFY_EMAIL.DESCRIPTION', { email }) }}
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('REGISTER.VERIFY_EMAIL.SPAM_HINT') }}
        </p>
      </div>
      <div class="space-y-4">
        <VueHcaptcha
          v-if="globalConfig.hCaptchaSiteKey"
          ref="hCaptcha"
          size="invisible"
          :sitekey="globalConfig.hCaptchaSiteKey"
          @verify="onCaptchaVerified"
          @error="onCaptchaError"
          @expired="onCaptchaError"
          @challenge-expired="onCaptchaError"
          @closed="onCaptchaError"
        />
        <NextButton
          lg
          type="button"
          data-testid="resend_email_button"
          class="w-full"
          :label="resendLabel"
          :is-loading="isResendingEmail"
          :disabled="cooldownSeconds > 0"
          @click="handleResendEmail"
        />
        <button
          type="button"
          class="w-full text-sm text-n-brand-text hover:underline"
          @click="goToSignup"
        >
          {{ $t('REGISTER.VERIFY_EMAIL.WRONG_EMAIL') }}
        </button>
      </div>
    </section>
  </main>
</template>
